import { isAllowedMediaUrl } from './docMediaHosts';
import { parseEmbedSrc, buildEmbedSrc } from './videoEmbeds';

const ALLOWED_TAGS = new Set([
  'p', 'h1', 'h2', 'h3', 'strong', 'b', 'em', 'i', 'u', 's', 'code', 'pre', 'blockquote', 'br', 'ul', 'ol', 'li', 'div', 'span', 'img', 'iframe',
  // Anchors: dedicated wipe-then-rebuild handling (sanitizeAnchor below) —
  // page-link chips and https links survive, everything else unwraps to text.
  'a',
  // Tables (Lexical @lexical/table round-trips via these tags).
  'table', 'thead', 'tbody', 'tr', 'th', 'td', 'colgroup', 'col',
]);

// Per-tag attribute allowlist (beyond img/iframe which have their own handling).
// Table cells need span attributes to preserve merged cells across a save/load.
// Checklists round-trip through `__lexicallisttype` on the <ul> plus
// `aria-checked` on leaf <li>s (@lexical/list exportDOM writes them; importDOM
// reads exactly these back) — strip either and every saved to-do silently
// reloads as a plain bullet list with all checked state gone. LOWERCASE on
// purpose: names here are compared against DOMParser output, and HTML
// attribute names are lowercased by the parser (`__lexicalListType` as written
// by exportDOM arrives as `__lexicallisttype`).
const KEEP_ATTRS = {
  th: ['colspan', 'rowspan'],
  td: ['colspan', 'rowspan'],
  col: ['span'],
  ul: ['__lexicallisttype'],
  li: ['role', 'aria-checked'],
};

/**
 * Encrypted document images (lexicalNodes/DocImageNode.js): the saved-HTML
 * attribute shapes, validated here on EVERY save/load round-trip and imported
 * by DocImageNode so its importDOM validates with the SAME patterns (single
 * source — the node module must not drift from the sanitizer).
 *
 *  - data-enc-key: base64 of a 32-byte AES key. 44 chars in standard base64;
 *    the 40–60 range plus the url-safe alphabet tolerates unpadded/url-safe
 *    variants without admitting anything long enough to smuggle markup.
 *  - data-enc: exactly the one algorithm this schema version defines.
 *  - data-pending-ref: outbox handle for a not-yet-uploaded image (opaque id).
 *  - data-mime / data-w / data-h / data-width / data-size / data-name: display
 *    metadata,
 *    each strictly shaped, all optional (w/h only ever together).
 */
export const DOC_MEDIA_ENC_ALG = 'aes-gcm-256';
export const DOC_MEDIA_ENC_KEY_RE = /^[A-Za-z0-9+/=_-]{40,60}$/;
export const DOC_MEDIA_PENDING_REF_RE = /^[A-Za-z0-9_-]{6,64}$/;
// Images, and since card 7de79076 (01.10.2026) the video containers the
// backend accepts for documents (.mp4 .webm .mov). Same encrypted node.
export const DOC_MEDIA_MIME_RE = /^(image\/[a-z0-9.+-]{1,30}|video\/(mp4|webm|quicktime))$/;
export const isDocVideoMime = (mime) => /^video\//.test(String(mime || ''));
export const DOC_MEDIA_DIM_RE = /^\d{1,5}$/;
export const DOC_MEDIA_SIZE_RE = /^\d{1,9}$/;
// data-name is DISPLAY text (a filename), not an identifier: instead of
// reject-on-mismatch it is stripped to this safe alphabet and capped at 128.
const DOC_MEDIA_NAME_STRIP_RE = /[^A-Za-z0-9._ -]+/g;

export function sanitizeDocMediaName(name) {
  return String(name || '').replace(DOC_MEDIA_NAME_STRIP_RE, '').slice(0, 128);
}

/**
 * A cell background colour, and nothing else.
 *
 * `style` is stripped everywhere in this sanitizer, which is the right default:
 * it is a large attack surface (`url(...)`, and historically `expression()`)
 * and almost nothing here needs it. Table cell backgrounds do — Lexical stores
 * them as inline style, so without this the colour is set in the editor and
 * silently discarded on save, which is worse than not offering the feature.
 *
 * So instead of allowing `style`, this REBUILDS it from one recognised
 * declaration whose value must match a strict colour literal. Anything else in
 * the attribute — a second declaration, a url(), a var() — is dropped rather
 * than escaped, because the output is a value we constructed and not user text
 * we tried to make safe.
 *
 * The value is read through `el.style`, so the CSS engine has already parsed it
 * and hands back a canonical `rgb(...)`. That is the point: validating the raw
 * attribute text would mean re-implementing colour parsing, and a parser that
 * disagrees with the browser's is exactly where sanitizers fail.
 */
const COLOR_LITERAL = /^(#[0-9a-f]{3,8}|rgba?\(\s*[\d.\s,%/]+\))$/i;

export function cellBackgroundStyle(el) {
  const declared = el.style?.backgroundColor || '';
  const value = declared.trim();
  if (!value || !COLOR_LITERAL.test(value)) return null;
  return `background-color: ${value}`;
}

// Page-link anchors (see lexicalNodes/PageLinkNode.js): both attributes must
// be present AND well-formed or the anchor is stripped to its text. The id
// charset covers crypto.randomUUID plus the legacy `id-<ts>-<hex>` fallback;
// 64 is a hard cap, not a target. The scope is exactly 'personal' or
// 'team:<digits>' — no other shape reaches the importer.
const DOC_LINK_ID = /^[A-Za-z0-9_.:-]{1,64}$/;
const DOC_LINK_SCOPE = /^(personal|team:\d{1,10})$/;
const CALLOUT_KIND = /^(info|warning|success)$/;

/**
 * Anchors, rebuilt-not-echoed (same posture as sanitizeImg/sanitizeIframe).
 * Exactly two shapes survive:
 *
 *  (a) a page-link chip — `data-doc-link` + `data-doc-scope`, both present and
 *      matching the strict patterns above (else: text). Any href riding along
 *      is wiped, never kept.
 *  (b) a normal link — `href` kept ONLY for http(s). This deliberately
 *      un-breaks plain links: before 'a' was allowlisted, EVERY link died on
 *      save (the tag was unwrapped to text), so LinkNode content never
 *      round-tripped. javascript:/data:/mailto:/relative/missing hrefs all
 *      unwrap to text.
 *
 * Returns false when the element should be UNWRAPPED TO ITS TEXT (unlike
 * img/iframe, where false means drop — a link's words must survive even when
 * its destination doesn't).
 */
function sanitizeAnchor(el) {
  const href = el.getAttribute('href') || '';
  const docLink = el.getAttribute('data-doc-link') || '';
  const docScope = el.getAttribute('data-doc-scope') || '';
  [...el.attributes].forEach((attr) => el.removeAttribute(attr.name));
  if (docLink || docScope) {
    if (DOC_LINK_ID.test(docLink) && DOC_LINK_SCOPE.test(docScope)) {
      el.setAttribute('data-doc-link', docLink);
      el.setAttribute('data-doc-scope', docScope);
      return true;
    }
    return false;
  }
  if (/^https?:\/\//i.test(href)) {
    el.setAttribute('href', href);
    return true;
  }
  return false;
}

/** The optional display metadata shared by both encrypted-image shapes —
 *  each attribute REBUILT only when it matches its strict pattern. */
function rebuildDocImageMeta(el, { alt, mime, w, h, size, name, width }) {
  if (alt) el.setAttribute('alt', alt);
  if (DOC_MEDIA_MIME_RE.test(mime)) el.setAttribute('data-mime', mime);
  // Both-or-neither: a lone dimension can't reserve an aspect-ratio box.
  if (DOC_MEDIA_DIM_RE.test(w) && DOC_MEDIA_DIM_RE.test(h)) {
    el.setAttribute('data-w', w);
    el.setAttribute('data-h', h);
  }
  // The user's chosen DISPLAY width, independent of the natural w/h above: it
  // is meaningful on its own (an image can be resized before its dimensions
  // are probed), so unlike w/h it is not both-or-neither. Same strict digit
  // pattern, so a hand-edited document cannot smuggle a style value through.
  if (DOC_MEDIA_DIM_RE.test(width)) el.setAttribute('data-width', width);
  if (DOC_MEDIA_SIZE_RE.test(size)) el.setAttribute('data-size', size);
  const safeName = sanitizeDocMediaName(name);
  if (safeName) el.setAttribute('data-name', safeName);
}

/**
 * Images, wipe-then-rebuild (same posture as sanitizeAnchor). Exactly three
 * shapes survive; everything else is dropped:
 *
 *  (a) LEGACY pasted data: image — `src` starts with data:image/ — keeps
 *      src+alt and nothing else (byte-for-byte the pre-media behavior; any
 *      enc-* attributes riding along are wiped, so a data: URL can never
 *      masquerade as an encrypted image).
 *  (b) UPLOADED encrypted doc image — allowlisted https `src`
 *      (utils/docMediaHosts.js) + a well-formed data-enc-key + the exact
 *      data-enc algorithm. Metadata rebuilt per-attribute above.
 *  (c) PENDING encrypted doc image — NO src at all, a well-formed
 *      data-pending-ref + the same key/alg validation. (A src that failed the
 *      allowlist does NOT fall through to pending: shape (b)'s rejection is
 *      final, or a foreign URL could survive as a "pending" node and come
 *      back to life later.)
 */
function sanitizeImg(el) {
  const src = el.getAttribute('src') || '';
  const alt = el.getAttribute('alt') || '';
  const encKey = el.getAttribute('data-enc-key') || '';
  const enc = el.getAttribute('data-enc') || '';
  const pendingRef = el.getAttribute('data-pending-ref') || '';
  const meta = {
    alt,
    mime: el.getAttribute('data-mime') || '',
    w: el.getAttribute('data-w') || '',
    h: el.getAttribute('data-h') || '',
    size: el.getAttribute('data-size') || '',
    name: el.getAttribute('data-name') || '',
    width: el.getAttribute('data-width') || '',
  };
  [...el.attributes].forEach((attr) => el.removeAttribute(attr.name));

  if (src.startsWith('data:image/')) {
    el.setAttribute('src', src);
    if (alt) el.setAttribute('alt', alt);
    return true;
  }

  const encOk = DOC_MEDIA_ENC_KEY_RE.test(encKey) && enc === DOC_MEDIA_ENC_ALG;
  if (src) {
    if (!encOk || !isAllowedMediaUrl(src)) return false;
    el.setAttribute('src', src);
    el.setAttribute('data-enc-key', encKey);
    el.setAttribute('data-enc', DOC_MEDIA_ENC_ALG);
    rebuildDocImageMeta(el, meta);
    return true;
  }
  if (encOk && DOC_MEDIA_PENDING_REF_RE.test(pendingRef)) {
    el.setAttribute('data-pending-ref', pendingRef);
    el.setAttribute('data-enc-key', encKey);
    el.setAttribute('data-enc', DOC_MEDIA_ENC_ALG);
    rebuildDocImageMeta(el, meta);
    return true;
  }
  return false;
}

/**
 * Video embeds: the src is PARSED (utils/videoEmbeds.js parseEmbedSrc — the
 * two providers, exact hosts, strict id charsets) and then REBUILT canonically
 * from the parsed parts, never echoed — junk query params are dropped and the
 * legacy `youtube.com/embed/` shape this function used to allowlist comes OUT
 * as `youtube-nocookie.com` (canonicalization on the next save). Presentation
 * attributes are forced, matching DocVideoEmbedNode.exportDOM exactly so a
 * round-trip through here is a no-op.
 */
function sanitizeIframe(el) {
  const parsed = parseEmbedSrc(el.getAttribute('src') || '');
  [...el.attributes].forEach((attr) => el.removeAttribute(attr.name));
  if (!parsed) return false;
  el.setAttribute('src', buildEmbedSrc(parsed));
  el.setAttribute('width', '560');
  el.setAttribute('height', '315');
  el.setAttribute('frameborder', '0');
  el.setAttribute('allowfullscreen', '');
  return true;
}

/**
 * Strips scripts and disallowed tags/attributes from rich-text HTML (shared
 * by RichTextEditor.js — Collection docs, Task cards, and any future
 * rich-text/notes usage). `img`/`iframe` get a per-tag attribute allowlist
 * instead of the default blanket attribute strip, since a pasted image or
 * YouTube embed needs its `src` preserved.
 */
export function sanitizeRichTextHtml(html) {
  if (!html || typeof html !== 'string') return '';

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const walk = (node) => {
    const children = [...node.childNodes];
    for (const child of children) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const tag = child.tagName.toLowerCase();
        if (!ALLOWED_TAGS.has(tag)) {
          const text = doc.createTextNode(child.textContent || '');
          child.replaceWith(text);
        } else if (tag === 'img') {
          if (!sanitizeImg(child)) child.remove();
        } else if (tag === 'iframe') {
          if (!sanitizeIframe(child)) child.remove();
        } else if (tag === 'a') {
          if (sanitizeAnchor(child)) {
            walk(child); // formatting inside a kept link is still sanitized
          } else {
            // Unwrap, don't drop: the link's words survive its destination.
            child.replaceWith(doc.createTextNode(child.textContent || ''));
          }
        } else {
          // Preserve the code-block language so highlighting can be restored on
          // reload (Lexical's @lexical/code round-trips via data-highlight-language).
          const keepLang =
            tag === 'pre' && child.getAttribute('data-highlight-language');
          const keptAttrs = (KEEP_ATTRS[tag] || [])
            .map((name) => [name, child.getAttribute(name)])
            .filter(([, value]) => value != null);
          // Read BEFORE the attributes are wiped — `el.style` is backed by the
          // attribute this loop is about to remove.
          const keptBg = tag === 'th' || tag === 'td' ? cellBackgroundStyle(child) : null;
          // Callout blocks (lexicalNodes/CalloutNode.js): the kind, and only
          // one of the three kinds — anything else is a plain <div>.
          const callout = tag === 'div' ? child.getAttribute('data-callout') : null;
          const keptCallout = CALLOUT_KIND.test(callout || '') ? callout : null;
          [...child.attributes].forEach((attr) => {
            child.removeAttribute(attr.name);
          });
          if (keepLang) child.setAttribute('data-highlight-language', keepLang);
          if (keptBg) child.setAttribute('style', keptBg);
          if (keptCallout) child.setAttribute('data-callout', keptCallout);
          keptAttrs.forEach(([name, value]) => child.setAttribute(name, value));
          walk(child);
        }
      }
    }
  };
  walk(doc.body);
  const sanitized = doc.body.innerHTML;
  // Trim wrapper whitespace only — do not strip spaces inside text nodes.
  return sanitized.replace(/^\s+/, '').replace(/\s+$/, '');
}
